import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import {useFormikContext} from 'formik';
import {Container, Divider, Row} from 'components-lib';
import {ResourceListColumn} from 'admin';
import {ResourceTypeNumEnum} from 'admin/resources/enums';
import {SelectionEnum} from 'filters-selections';
import {useFileEditPermissions} from '../../hooks';
import {IEditFileMetadataFormBaseValues} from '../FilesEditResourceForm/config';

export const FilesEditResource = () => {
  const classes = useStyles();
  const {hasFileEditPermissions} = useFileEditPermissions();
  const {values} = useFormikContext<IEditFileMetadataFormBaseValues>();
  const isImageFileType = values?.fileType === ResourceTypeNumEnum.Image;
  const disableAll = !hasFileEditPermissions;

  const block1Left = useMemo(
    () => [
      SelectionEnum.DisplayNameInput,
      SelectionEnum.AssetIdLabel,
      SelectionEnum.FileNameLabel,
      SelectionEnum.DirectoryLinkInput,
      SelectionEnum.VersionFileUpload,
    ],
    []
  );

  const block1Right = useMemo(
    () => [SelectionEnum.FileTypeLabel, SelectionEnum.LanguagesMulti, SelectionEnum.BriefDescriptionTextArea],
    []
  );

  const block2Left = useMemo(
    () => [
      SelectionEnum.ProgramMultiSelection,
      SelectionEnum.SessionThemeNumberInput,
      SelectionEnum.SlideNumberInput,
      SelectionEnum.TagGradeLevels,
    ],
    []
  );

  const block2Right = useMemo(() => [SelectionEnum.KeywordsTextArea, SelectionEnum.AssociatedResourcesMultiSelect], []);

  const block3Left = useMemo(
    () => [
      SelectionEnum.CharacterTraitsMultiSelect,
      SelectionEnum.PillarMultiSelect,
      SelectionEnum.CompetenciesByPillarWithInfo,
      SelectionEnum.KnowledgeSkillsAndAbilitiesWithInfo,
      SelectionEnum.BehavioralIndicatorsMultiSelect,
      SelectionEnum.LearningObjectivesTextArea,
    ],
    []
  );

  const block3Right = useMemo(
    () =>
      isImageFileType
        ? [
            SelectionEnum.ImageSourcesMultiSelect,
            SelectionEnum.LinkToStockImageSourceInput,
            SelectionEnum.PublishedOutputToolSelect,
            SelectionEnum.AlternateTextArea,
            SelectionEnum.VideoTranscriptionLinkInput,
            SelectionEnum.GendersMultiSelect,
            SelectionEnum.EthnicitiesMultiSelect,
          ]
        : [],
    [isImageFileType]
  );

  const block4Left = useMemo(
    () => [
      SelectionEnum.CredentialingCheckbox,
      SelectionEnum.AccessibleToStudentsWithInfo,
      SelectionEnum.AccessibilityStatusRadioGroup,
      SelectionEnum.AccessibilityReportsInput,
      SelectionEnum.AdminPermissionCheckbox,
    ],
    []
  );

  const block4Right = useMemo(
    () => [
      SelectionEnum.PermissionNeededRadioGroup,
      SelectionEnum.ExpirationWithInfoDatePicker,
      SelectionEnum.ReuseFlagRadioGroup,
      SelectionEnum.PermissionAgreementUrlInput,
      SelectionEnum.UrlSourceInput,
      SelectionEnum.NotesTextArea,
    ],
    []
  );

  return (
    <Container disableGutters>
      <Row justify="flex-start" align="flex-start">
        <ResourceListColumn list={block1Left} disableAll={disableAll} sm={6} md={6} lg={6} xl={5} />
        <ResourceListColumn list={block1Right} disableAll={disableAll} sm={6} md={6} lg={6} xl={5} />
      </Row>

      <Divider.Horizontal as="div" fullWidth />

      <Row justify="flex-start" align="flex-start" classes={{root: classes.block}}>
        <ResourceListColumn list={block2Left} disableAll={disableAll} sm={6} md={6} lg={6} xl={5} />
        <ResourceListColumn list={block2Right} disableAll={disableAll} sm={6} md={6} lg={6} xl={5} />
      </Row>

      <Divider.Horizontal as="div" fullWidth />

      <Row justify="flex-start" align="flex-start" classes={{root: classes.block}}>
        <ResourceListColumn list={block3Left} disableAll={disableAll} sm={6} md={6} lg={6} xl={5} />
        {isImageFileType && (
          <ResourceListColumn list={block3Right} disableAll={disableAll} sm={6} md={6} lg={6} xl={5} />
        )}
      </Row>

      <Divider.Horizontal as="div" fullWidth />

      <Row justify="flex-start" align="flex-start" classes={{root: classes.block}}>
        <ResourceListColumn list={block4Left} disableAll={disableAll} sm={6} md={6} lg={6} xl={5} />
        <ResourceListColumn list={block4Right} disableAll={disableAll} sm={6} md={6} lg={6} xl={5} />
      </Row>
    </Container>
  );
};

const useStyles = makeStyles((theme) => ({
  block: {
    marginTop: theme.spacing(2),
  },
}));
